import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import InfoIcon from "../../svg/InfoIcon";
import CustomTooltip from "./CustomTooltip";

type Props = {
  size: number;
  strokeWidth: number;
  value: number | undefined;
  color?: string;
  backgroundColor?: string;
  label?: string;
  isMediumChart?: boolean;
  isMainChart?: boolean;
  initialValue?: number;
  type?: string;
  totalValue?: number;
};

const CircularGraph = ({
  size,
  strokeWidth,
  value = 0,
  color,
  backgroundColor,
  label,
  isMediumChart = false,
  isMainChart = false,
  initialValue,
  type,
  totalValue,
}: Props) => {
  const theme = useTheme();
  const radius = (size - strokeWidth - 1) / 2;
  const circumference = radius * 2 * Math.PI;

  const svgProgress = 100 - (value / 100) * 100;

  let tooltipMessage = "";

  if (label === "Activity") {
    tooltipMessage =
      "The Activity score indicates whether your calorie burn level and type of workout are helping you achieve your fitness and health goals. It takes into account your active calories and whether you perform the prescribed type of activity. To close your activity ring during rest days, you only need to hit your general activities calorie target.";
  } else if (label === "Nutrition") {
    tooltipMessage =
      "The Nutrition score indicates how beneficial your nutrition is for your fitness goal and overall well-being. It’s calculated based on the quality and quantity of food items you log. Quantity means that it accounts for the calories and quality for protein, fats, and carbohydrates you log compared to your targets.";
  } else if (label === "Recovery") {
    tooltipMessage =
      "The Recovery score indicates how well-rested your body is based on your sleep duration and quality, resting heart rate and HRV. Your sleep quality takes into account the time you spent in different sleep stages (e.g., light, deep sleep, etc).";
  } else {
    tooltipMessage = "";
  }

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          stroke={backgroundColor ?? theme.colorTokens.N[40]}
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          stroke={color}
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={circumference * (svgProgress / 100)}
          strokeLinecap="round"
          strokeWidth={strokeWidth}
          style={{
            transition: "stroke-dashoffset 0.35s",
            transform: "rotate(-90deg)",
            transformOrigin: "50% 50%",
          }}
        />
        {initialValue && (
          <circle
            stroke={theme.colorTokens.W[60]}
            fill="none"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeDasharray={`${circumference * 0.001} ${circumference}`}
            strokeDashoffset={circumference * (1 - initialValue / 100)}
            strokeLinecap="square"
            transform={`rotate(-90, ${size / 2}, ${size / 2})`}
            strokeWidth={strokeWidth}
          />
        )}
      </svg>
      {type === "nextBreathAnalysis" && (
        <div style={{ ...styles.textContainer(), width: "100px" }}>
          <div style={styles.text(theme, isMainChart, isMediumChart)}>
            {totalValue ? totalValue : 0}
          </div>
          <div style={styles.label(theme, isMainChart)}>
            {totalValue === 1 ? "day left" : "days left"}
          </div>
        </div>
      )}
      {type === "fuelUsage" && (
        <div style={styles.textContainer()}>
          <div style={styles.labelFuelUsage(theme)}>{`${totalValue} kcal`}</div>
          <div style={styles.value(theme)}>Burn</div>
        </div>
      )}
      {type !== "fuelUsage" && type !== "nextBreathAnalysis" && (
        <div style={styles.textContainer()}>
          <div style={styles.text(theme, isMainChart, isMediumChart)}>
            {value}
            <span style={styles.valueUnit(theme, isMainChart, isMediumChart)}>
              %
            </span>
          </div>
          {(isMediumChart || isMainChart) && (
            <div style={styles.label(theme, isMainChart)}>
              {label}
              {isMainChart && (
                <div style={styles.infoIconContainer()}>
                  <CustomTooltip message={tooltipMessage}>
                    <InfoIcon color={theme.colors_ds.PRIMARY_ICON_MAIN} />
                  </CustomTooltip>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CircularGraph;

const styles = {
  textContainer: () => ({
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
  }),
  text: (theme: any, isMainChart: boolean, isMediumChart: boolean) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: isMainChart ? "16px" : isMediumChart ? "16px" : "10px",
    textAlign: "center" as "center",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  valueUnit: (theme: any, isMainChart: boolean, isMediumChart: boolean) => ({
    fontFamily:
      isMediumChart || isMainChart ? "var(--font-Medium)" : "var(--font-Bold)",
    color: isMainChart
      ? theme.colors_ds.NEUTRAL_TEXT_WEAK
      : theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  label: (theme: any, isMainChart: boolean) => ({
    fontFamily: isMainChart ? "var(--font-Bold)" : "var(--font-Medium)",
    color: isMainChart
      ? theme.colors_ds.NEUTRAL_TEXT_DEFAULT
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontSize: isMainChart ? "14px" : "12px",
    marginTop: "2px",
  }),
  infoIconContainer: () => ({
    position: "absolute" as "absolute",
    height: "16px",
    top: "25%",
    right: "-4px",
    transform: "translateY(-50%)",
    zIndex: 100,
    background: "none",
    border: "none",
    cursor: "pointer",
  }),
  labelFuelUsage: (theme: any) => ({
    width: "100px",
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    textAlign: "center" as "center",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  value: (theme: any) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    textAlign: "center" as "center",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
};
